import { useEffect, useLayoutEffect, useRef, useState } from "react";

type TripperProgressPillProps = {
  dayNumber: number;
  distance: string;
  progress: number;
  isComplete?: boolean;
  label?: string;
  onToggleUnit?: () => void;
};

const BG = "#fffdf6";
const TRACK_RGB = [
  [249, 174, 119],
  [236, 203, 96],
  [146, 191, 219],
  [196, 185, 224],
];
const BG_RGB = [
  [255, 231, 206],
  [250, 238, 198],
  [225, 236, 235],
  [240, 234, 236],
];
const COMPLETE_TRACK = "#BEC97E";
const COMPLETE_BG = "#EDEECF";

const PW = 196;
const PH = 48;
const SW = 4;
const INSET = SW / 2;
const PR = (PH - SW) / 2;

const clampProgress = (value: number) => Math.min(Math.max(value, 0), 0.998);

const lerpColors = (palette: number[][], t: number) => {
  const boundaries = [0.25, 0.5, 0.75];
  const halfTrans = 0.035;
  let color = palette[0];

  for (let i = 0; i < boundaries.length; i += 1) {
    const boundary = boundaries[i];
    if (t > boundary + halfTrans) {
      color = palette[i + 1];
    } else if (t > boundary - halfTrans) {
      const f = (t - (boundary - halfTrans)) / (2 * halfTrans);
      const smooth = f * f * (3 - 2 * f);
      color = palette[i].map((v, k) =>
        Math.round(v + (palette[i + 1][k] - v) * smooth),
      );
    }
  }

  return color;
};

const rgb = (c: number[]) => `rgb(${c[0]},${c[1]},${c[2]})`;

const pillD = () => {
  const i = INSET;
  const w = PW - SW;
  const h = PH - SW;
  const r = PR;
  const cx = PW / 2;

  return [
    `M ${cx} ${i}`,
    `L ${i + w - r} ${i}`,
    `A ${r} ${r} 0 0 1 ${i + w} ${i + r}`,
    `A ${r} ${r} 0 0 1 ${i + w - r} ${i + h}`,
    `L ${i + r} ${i + h}`,
    `A ${r} ${r} 0 0 1 ${i} ${i + r}`,
    `A ${r} ${r} 0 0 1 ${i + r} ${i}`,
    `L ${cx} ${i}`,
  ].join(" ");
};

const PILL_D = pillD();

export const TripperProgressPill = ({
  dayNumber,
  distance,
  progress,
  isComplete = false,
  label,
  onToggleUnit,
}: TripperProgressPillProps) => {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const pathRef = useRef<SVGPathElement | null>(null);
  const dotRef = useRef<SVGCircleElement | null>(null);
  const pointsRef = useRef<Array<{ x: number; y: number; t: number }>>([]);
  const ctxRef = useRef<CanvasRenderingContext2D | null>(null);
  const rafRef = useRef<number | null>(null);

  const displayRef = useRef(0);
  const targetRef = useRef(0);
  const hasStartedRef = useRef(false);
  const prevDayRef = useRef(dayNumber);
  const dayTransitionRef = useRef<{
    fromColor: number[];
    fade: number;
  } | null>(null);

  const [ready, setReady] = useState(false);
  const [textPhase, setTextPhase] = useState<"idle" | "exiting" | "entering">("idle");
  const [displayDay, setDisplayDay] = useState(dayNumber);
  const [displayLabel, setDisplayLabel] = useState(label);
  const [displayDistance, setDisplayDistance] = useState(distance);
  const [displayComplete, setDisplayComplete] = useState(isComplete);
  const prevCompleteRef = useRef(isComplete);
  const [bouncing, setBouncing] = useState(false);
  const textEnterRef = useRef<HTMLDivElement | null>(null);

  const reducedMotion =
    typeof window !== "undefined" &&
    window.matchMedia &&
    window.matchMedia("(prefers-reduced-motion: reduce)").matches;

  if (progress > 0.01) {
    hasStartedRef.current = true;
  }

  const clampedProgress = clampProgress(progress);
  targetRef.current = clampedProgress;

  useEffect(() => {
    if (dayNumber !== prevDayRef.current) {
      const wentBack = dayNumber < prevDayRef.current;
      if (wentBack) {
        displayRef.current = clampedProgress;
        targetRef.current = clampedProgress;
        dayTransitionRef.current = {
          fromColor: lerpColors(TRACK_RGB, 0),
          fade: 0,
        };
      }
      prevDayRef.current = dayNumber;
    }
  }, [dayNumber, clampedProgress]);

  useEffect(() => {
    const canvas = canvasRef.current;
    const path = pathRef.current;
    if (!canvas || !path) return;

    const dpr = window.devicePixelRatio || 1;
    canvas.width = PW * dpr;
    canvas.height = PH * dpr;
    canvas.style.width = `${PW}px`;
    canvas.style.height = `${PH}px`;

    const ctx = canvas.getContext("2d");
    if (!ctx) return;
    ctx.scale(dpr, dpr);
    ctxRef.current = ctx;

    const totalLen = path.getTotalLength();
    const step = 1.4;
    const n = Math.ceil(totalLen / step);
    const points: Array<{ x: number; y: number; t: number }> = [];
    for (let i = 0; i <= n; i += 1) {
      const t = i / n;
      const point = path.getPointAtLength(t * totalLen);
      points.push({ x: point.x, y: point.y, t });
    }
    pointsRef.current = points;
    setReady(true);
  }, []);

  useLayoutEffect(() => {
    const ctx = ctxRef.current;
    const points = pointsRef.current;
    if (!ctx || points.length === 0) return;

    ctx.clearRect(0, 0, PW, PH);
    if (clampedProgress < 0.003) return;

    ctx.lineWidth = SW;
    ctx.lineCap = "butt";
    const visibleIdx = Math.floor(clampedProgress * (points.length - 1));

    let trackColor = rgb(lerpColors(TRACK_RGB, clampedProgress));
    if (isComplete) {
      trackColor = COMPLETE_TRACK;
    } else {
      const transition = dayTransitionRef.current;
      if (transition && transition.fade < 1) {
        const targetColor = lerpColors(TRACK_RGB, clampedProgress);
        const f = transition.fade;
        trackColor = rgb([
          Math.round(transition.fromColor[0] * (1 - f) + targetColor[0] * f),
          Math.round(transition.fromColor[1] * (1 - f) + targetColor[1] * f),
          Math.round(transition.fromColor[2] * (1 - f) + targetColor[2] * f),
        ]);
      }
    }

    ctx.strokeStyle = trackColor;
    for (let i = 0; i < visibleIdx; i += 1) {
      const p1 = points[i];
      const p2 = points[i + 1];
      if (!p2) break;
      const dx = p2.x - p1.x;
      const dy = p2.y - p1.y;
      const segLen = Math.sqrt(dx * dx + dy * dy);
      const overlap = segLen > 0 ? 0.3 / segLen : 0;
      ctx.beginPath();
      ctx.moveTo(p1.x, p1.y);
      ctx.lineTo(p2.x + dx * overlap, p2.y + dy * overlap);
      ctx.stroke();
    }
  }, [clampedProgress, ready, isComplete]);

  useEffect(() => {
    if (!ready) return;

    if (reducedMotion) {
      displayRef.current = targetRef.current;
      const dot = dotRef.current;
      const points = pointsRef.current;
      if (dot && points.length > 0 && hasStartedRef.current) {
        const idx = Math.min(
          Math.floor(clampProgress(displayRef.current) * (points.length - 1)),
          points.length - 1,
        );
        const point = points[idx];
        dot.setAttribute("cx", point.x.toString());
        dot.setAttribute("cy", point.y.toString());
        dot.setAttribute("fill", rgb(lerpColors(BG_RGB, point.t)));
        dot.setAttribute("stroke", rgb(lerpColors(TRACK_RGB, point.t)));
      }
      return;
    }

    let lastTime = 0;
    const EASE = 12;

    const tick = (now: number) => {
      rafRef.current = window.requestAnimationFrame(tick);

      const dt = lastTime ? Math.min((now - lastTime) / 1000, 0.05) : 0.016;
      lastTime = now;

      const transition = dayTransitionRef.current;
      if (transition && transition.fade < 1) {
        transition.fade = Math.min(transition.fade + dt * 2.5, 1);
        if (transition.fade >= 1) {
          dayTransitionRef.current = null;
        }
      }

      const target = targetRef.current;
      const current = displayRef.current;
      const diff = target - current;
      if (diff < -0.3) {
        displayRef.current = target;
      } else {
        const step = 1 - Math.exp(-EASE * dt);
        displayRef.current = current + diff * step;
        if (Math.abs(diff) < 0.001) {
          displayRef.current = target;
        }
      }

      const dot = dotRef.current;
      const points = pointsRef.current;
      if (!dot || points.length === 0 || !hasStartedRef.current) return;

      const p = clampProgress(displayRef.current);
      const idx = Math.min(
        Math.floor(p * (points.length - 1)),
        points.length - 1,
      );
      const point = points[idx];
      dot.setAttribute("cx", point.x.toString());
      dot.setAttribute("cy", point.y.toString());

      if (transition && transition.fade < 1) {
        const f = transition.fade;
        const targetBg = lerpColors(BG_RGB, point.t);
        const targetStroke = lerpColors(TRACK_RGB, point.t);
        const fromBg = lerpColors(BG_RGB, 0);
        const fromStroke = transition.fromColor;
        dot.setAttribute(
          "fill",
          rgb([
            Math.round(fromBg[0] * (1 - f) + targetBg[0] * f),
            Math.round(fromBg[1] * (1 - f) + targetBg[1] * f),
            Math.round(fromBg[2] * (1 - f) + targetBg[2] * f),
          ]),
        );
        dot.setAttribute(
          "stroke",
          rgb([
            Math.round(fromStroke[0] * (1 - f) + targetStroke[0] * f),
            Math.round(fromStroke[1] * (1 - f) + targetStroke[1] * f),
            Math.round(fromStroke[2] * (1 - f) + targetStroke[2] * f),
          ]),
        );
      } else {
        dot.setAttribute("fill", rgb(lerpColors(BG_RGB, point.t)));
        dot.setAttribute("stroke", rgb(lerpColors(TRACK_RGB, point.t)));
      }
    };

    rafRef.current = window.requestAnimationFrame(tick);
    return () => {
      if (rafRef.current) {
        window.cancelAnimationFrame(rafRef.current);
      }
    };
  }, [ready, reducedMotion]);

  useEffect(() => {
    const dayChanged = dayNumber !== displayDay;
    const labelChanged = label !== displayLabel;
    const completeChanged = isComplete !== prevCompleteRef.current;
    if (!dayChanged && !labelChanged && !completeChanged) return;

    prevCompleteRef.current = isComplete;
    setTextPhase("exiting");
    const exitTimer = window.setTimeout(() => {
      setDisplayDay(dayNumber);
      setDisplayLabel(label);
      setDisplayDistance(distance);
      setDisplayComplete(isComplete);
      setTextPhase("entering");
      window.setTimeout(() => {
        setTextPhase("idle");
      }, 280);
    }, 240);

    return () => window.clearTimeout(exitTimer);
  }, [dayNumber, label, distance, isComplete, displayDay, displayLabel]);

  useEffect(() => {
    if (textPhase === "idle" && dayNumber === displayDay && !isComplete) {
      setDisplayDistance(distance);
    }
  }, [distance, textPhase, dayNumber, displayDay, isComplete]);

  useLayoutEffect(() => {
    if (textPhase === "entering" && textEnterRef.current) {
      const element = textEnterRef.current;
      element.style.transition = "none";
      element.style.opacity = "0";
      element.style.transform = "translateY(10px)";
      element.getBoundingClientRect();
      element.style.transition = "opacity .28s ease-out, transform .28s ease-out";
      element.style.opacity = "1";
      element.style.transform = "translateY(0px)";
    }
  }, [textPhase]);

  const textStyle =
    textPhase === "exiting"
      ? {
          opacity: 0,
          transform: "translateY(-10px)",
          transition: "opacity .24s ease-in, transform .24s ease-in",
        }
      : textPhase === "entering"
        ? {
            opacity: 1,
            transform: "translateY(0px)",
            transition: "opacity .28s ease-out, transform .28s ease-out",
          }
        : {
            opacity: 1,
            transform: "translateY(0px)",
            transition: "none",
          };

  const pillBg = isComplete
    ? COMPLETE_BG
    : hasStartedRef.current && clampedProgress > 0.003
      ? rgb(lerpColors(BG_RGB, clampedProgress))
      : BG;

  return (
    <div
      className="tripper-progress-pill"
      style={{
        background: pillBg,
        cursor: onToggleUnit && !isComplete ? "pointer" : undefined,
        transform: bouncing ? "scale(0.95)" : "scale(1)",
        transition: "transform 150ms cubic-bezier(0.34, 1.8, 0.64, 1)",
      }}
      onClick={onToggleUnit && !isComplete ? () => {
        onToggleUnit();
        setBouncing(true);
        window.setTimeout(() => setBouncing(false), 150);
      } : undefined}
      role={onToggleUnit && !isComplete ? "button" : undefined}
      tabIndex={onToggleUnit && !isComplete ? 0 : undefined}
      onKeyDown={onToggleUnit && !isComplete ? (e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); onToggleUnit(); } } : undefined}
    >
      <svg
        className="tripper-progress-pill__svg"
        width={PW}
        height={PH}
        viewBox={`0 0 ${PW} ${PH}`}
        aria-hidden="true"
      >
        <path
          d={PILL_D}
          fill="none"
          stroke="var(--tripper-border, #d8dbe5)"
          strokeWidth={SW}
        />
        <path ref={pathRef} d={PILL_D} fill="none" stroke="none" />
      </svg>
      <canvas
        ref={canvasRef}
        className="tripper-progress-pill__canvas"
        aria-hidden="true"
      />
      {hasStartedRef.current && (
        <svg
          className="tripper-progress-pill__dot"
          width={PW}
          height={PH}
          viewBox={`0 0 ${PW} ${PH}`}
          style={{ position: "absolute", top: 0, left: 0, overflow: "visible", pointerEvents: "none" }}
          aria-hidden="true"
        >
          <circle
            ref={dotRef}
            cx={PW / 2}
            cy={INSET}
            r={5.5}
            fill={rgb(lerpColors(BG_RGB, clampedProgress))}
            stroke={rgb(lerpColors(TRACK_RGB, clampedProgress))}
            strokeWidth={2}
          />
        </svg>
      )}
      <div ref={textEnterRef} className="tripper-progress-pill__text" style={textStyle}>
        {displayComplete ? (
          <span className="tripper-progress-pill__label">End of itinerary</span>
        ) : displayLabel !== undefined && displayLabel !== null ? (
          <span className="tripper-progress-pill__distance">{displayDistance}</span>
        ) : (
          <>
            <span className="tripper-progress-pill__label">Day {displayDay}</span>
            <div className="tripper-progress-pill__divider" />
            <span className="tripper-progress-pill__distance">{displayDistance}</span>
          </>
        )}
      </div>
    </div>
  );
};
